/**
 * @fileoverview The subscribe page: the plans, and where you stand.
 *
 * The paid plan is Grow More (Grow is the app's name; decided 2026-10-07).
 * During the trial it says how long is left and offers the plans: yearly
 * first, "$39 a year · $3.25 a month", marked Best value, with the launch
 * prices said plainly from January until they end ("$39 for your first year
 * (the server's finished sentence, plans.launch.words, L6: "Join before April 1 and lock in $39 for your first year."; no price after it)
 * 1.", no countdown; the server leaves it out before
 * subscription.SHOW_LAUNCH_LINE_FROM; with it, a terracotta "Off-season price,
 * until April 1" pill tops the yearly card, as on the landing page) or, for a founding gardener, that the
 * founder price is for their first year (said whole by the server in the gardener's language,
 * plans.founderPriceWords, C101; no price is promised for as long as
 * they stay; decided 2026-10-07); then "or $7.99 a month, pause any time"
 * under it ($8.99 from April 1, as the server says); then the plans in three lines (PlanLines:
 * "Free: up to 5 plants, for as long as you like." · "Grow More, $39 a year
 * or $7.99 a month: unlimited plants, warning emails, photo identification,
 * seed-packet scans, plant check-ups." · "Try Grow More free for 14 days
 * from your first plant.", the last only while the trial waits for that
 * plant); then Free and Grow More side by side, with ticks
 * (PlanComparison), the AI in one plain row (owner, 2026-10-08: no allowance
 * words and no Fair use link here; the allowance is in the terms and the
 * in-app notes, never "unlimited"). On Free (a trial
 * or subscription that ended) it says
 * what Free is and offers the plans again; never read-only.
 *
 * A founding gardener in their free period (the server's status line is
 * 'founding'; access.canChoosePlan false) is offered no plan to pay for: the yearly card
 * keeps its founder price words but has no "Choose" button, and the monthly
 * link and the "after the trial" note are left out, and the server's plans.display takes away the saving
 * line and the Best value pill and says the plan line as the founder's ("Grow More: free until June 30,
 * 2027, then $39 for your first year"; B69). Their status line
 * says free until the founding day, then the founder price.
 *
 * Subscribed, it says so and opens Stripe's portal for changes. A monthly
 * subscriber who goes to cancel ("Cancel subscription", offered when the
 * server says access.canCancel: a running monthly plan, trialing included)
 * is offered a pause first ("Pause until spring instead?", PauseOffer) when
 * the server says a pause may be offered (access.canPause), and otherwise
 * goes straight to Stripe's cancel step; /subscribe?pause=1 (the price
 * notice email's "Pause instead" link) opens the pause offer too. Paused, it says until when, with "Resume now"
 * and what resuming charges today: the month as Stripe charges it, a first
 * year's discount taken off (GET /billing/pause's price), not the list price;
 * with a pause ahead, "Don't pause".
 *
 * A gardener whose trial is a gift of a year is told who gave it and until
 * when ("Amy gave you a year of Grow More…"), with the plans offered as in a
 * trial. Under everything, "Have a gift code?" redeems one (GiftCodeBox),
 * and links to giving one (/gift).
 *
 * A member tending someone else's garden (the status's tendingFor; B23,
 * owner 2026-10-20: "the invitee should only use the inviters trial not their
 * own") is shown that owner's plan and the server's line saying so
 * (TendingPlan), with no purchase buttons, notes, gift code box or pause
 * offer: a member never acts on the owner's subscription (the server refuses
 * it too, 403 TENDING_OWNER_PLAN). Their own plan is managed from their own
 * garden.
 *
 * Prices come from Stripe through the backend, so the number here is the
 * number charged.
 *
 * The server decides what the page says about where the gardener stands
 * (access.statusLine: a kind and its days, which StatusLine maps to the
 * billing.status.* words and only formats), whether the pause is offered on
 * the way to cancelling (access.canPause, the server's pause rule), the
 * comparison's rows (plans.rows) and Free's plants (access.limits.plants,
 * plans.free); the page works none of them out (architecture audit batch 8).
 *
 * Where: the route /subscribe, inside the Layout (the header's TrialLine,
 * the wall's "See plans" and every Subscribe link go here).
 * API: GET /billing/status (useBilling); POST /billing/checkout { interval }
 * and POST /billing/portal (then the browser leaves for Stripe); GET
 * /billing/pause (the resume price, only while paused); POST /billing/resume.
 * PauseOffer and GiftCodeBox make their own calls.
 * Settings: the server's plans (Stripe prices; plans.launch until
 * subscription.SHOW_LAUNCH_LINE_FROM; plans.free.plants
 * and access.limits.plants.limit from trial.FREE_PLANTS; plans.rows);
 * data.configured false (no Stripe keys) disables
 * every button and says billing.notConfigured. ?pause=1 opens the pause
 * offer, ?cancelled=1 (back from Checkout) says billing.cancelled.
 * Words (en/es): billing.title, billing.status.*, billing.plan.*,
 * billing.lines.*, billing.compare.* (PlanComparison), billing.pause.*, billing.notes.*, billing.manage*, billing.choose,
 * billing.startFailed, billing.loadFailed, billing.cancelled.
 * A failure on our side (a 500, no connection; D31, lib/serverFailure) says
 * the standard sentence, reported to Sentry, with the support address as a
 * link and no "Try again in a moment": in place of billing.loadFailed when
 * the status could not be read, and of billing.startFailed or
 * billing.pause.failed when checkout, the portal or resuming could not start.
 * Watch out: the yearly card's price is plans.year (a first-year price before
 * the launch ends: "$39 for your first year"), not the list price, and the
 * "Saves" line compares it with twelve monthly charges.
 *
 * @module SubscribePage
 */

import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Snowflake } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Pill } from '@/shared/components/ui/Pill';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure, rateLimitedText } from '@/shared/lib/serverFailure';
import { FailureText, ServerFailureNote } from '@/shared/components/ServerFailure';
import {
  getPauseOffer, openPortal, resumePlan, startCheckout, type BillingInterval, type BillingStatus, type Price, type StatusLine,
} from '@/shared/api/billing';
import { cn } from '@/lib/utils';
import { DateTime } from '@/shared/utils/dateUtils';
import { BILLING_QUERY_KEY, formatPrice, useBilling, useTendingFor } from './lib/useBilling';
import { PlanComparison } from './components/PlanComparison';
import { PauseOffer } from './components/PauseOffer';
import { TendingPlan } from './components/TendingPlan';
import { pauseDay } from './lib/pauseDay';
import { foundingDay } from './lib/foundingDay';
import { GiftCodeBox } from '@/features/gift/components/GiftCodeBox';
import { usePageRestored } from '@/shared/hooks/usePageRestored';

/**
 * A date from the billing status as "3 October 2026" ("3 octubre 2026"), in the app's language, or null when there
 * is none or it cannot be read.
 *
 * @param iso - From the server.
 * @param language - The app's language.
 */
const dayOf = (iso: string | null | undefined, language: string): string | null => {
  if (!iso) return null;
  const at = DateTime.fromISO(iso);
  return at.isValid ? at.setLocale(language).toFormat('d LLLL yyyy') : null;
};

/** The pause offer's cache key: under the billing status's, so resuming (which refreshes that) refreshes it too. */
const PAUSE_OFFER_QUERY_KEY = [...BILLING_QUERY_KEY, 'pause'] as const;

/**
 * A YYYY-MM-DD day from the server as "February 28" (or with its year), in the app's language; the text as it
 * came when it does not read.
 *
 * @param day - A day, not a moment.
 * @param language - The app's language.
 * @param withYear - Say the year too.
 */
const calendarDay = (day: string | null | undefined, language: string, withYear = false): string => {
  if (!day) return '';
  const at = DateTime.fromISO(day);
  if (!at.isValid) return day;
  return at.setLocale(language).toLocaleString(withYear ? { month: 'long', day: 'numeric', year: 'numeric' } : { month: 'long', day: 'numeric' });
};

/**
 * The words for the server's status line: its kind to billing.status.<kind>, with its days and numbers
 * formatted for the reader.
 *
 * @param line - access.statusLine.
 * @param data - The billing status, for the founder's yearly price.
 * @param t - The translator.
 * @param language - The app's language.
 */
function statusWords(
  line: StatusLine, data: BillingStatus, t: (key: string, params?: Record<string, string | number>) => string, language: string,
): string | null {
  switch (line.kind) {
    case 'paused': return t('billing.status.paused', { date: pauseDay(line.until, language) });
    case 'pausedOpen': return t('billing.status.pausedOpen');
    case 'ending': return t('billing.status.ending', { date: dayOf(line.date, language) ?? '' });
    case 'paymentProblem': return t('billing.status.paymentProblem');
    case 'pauseAhead': return t('billing.status.pauseAhead', { from: pauseDay(line.from, language), until: pauseDay(line.until, language) });
    case 'active':
      return t('billing.status.active', {
        plan: line.plan === 'year' ? t('billing.plan.yearly') : t('billing.plan.monthly'),
        date: dayOf(line.date, language) ?? '',
      });
    // A founding gardener: no countdown; free until the founding day, then the founder price.
    case 'founding': return t('billing.status.founding', { date: foundingDay(line.date, language), price: data.plans.founderPriceWords ?? formatPrice(data.plans.year) });
    // A gift of a year: who gave it and its last day; nothing renews by itself.
    case 'gift': return t('billing.status.gift', { from: line.giftFrom ?? '', date: calendarDay(line.date, language, true) });
    // The trial from the first plant waits for it.
    case 'trialWaiting': return t('billing.status.trialWaiting', { days: line.days ?? data.trialDays });
    case 'trialOne': return t('billing.status.trialOne');
    case 'trial': return t('billing.status.trial', { days: line.days ?? 0 });
    case 'free': return t('billing.status.free', { plants: line.plants ?? '' });
    default: return null;
  }
}

/**
 * Where the gardener stands, in one paragraph: the server's status line, in the app's words.
 *
 * @param props.data - The billing status.
 */
const StatusLineText: React.FC<{ data: BillingStatus }> = ({ data }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const text = statusWords(data.access.statusLine, data, t, language);
  return text ? <p className="mt-2 max-w-prose text-muted-foreground">{text}</p> : null;
};

/**
 * The plans in three lines, as the owner and marketing agreed them: Free,
 * Grow More with its prices, and the trial (only while it waits for the
 * first plant; a founding gardener is never offered it).
 *
 * @param props.data - The billing status.
 * @param props.year - The yearly price shown.
 * @param props.month - The monthly price shown.
 */
const PlanLines: React.FC<{ data: BillingStatus; year: Price; month: Price }> = ({ data, year, month }) => {
  const { reactTranslate: t } = useTranslation();
  const { access } = data;
  return (
    <ul className="mt-6 space-y-1.5 text-sm">
      <li>{t('billing.lines.free', { plants: access.limits.plants.limit })}</li>
      <li>
        {data.plans.display.line === 'founder'
          ? t('billing.lines.growFounder')
          : t('billing.lines.grow', { year: formatPrice(year), month: formatPrice(month) })}
      </li>
      {access.showTrialLine && <li className="text-muted-foreground">{t('billing.lines.trial', { days: access.daysLeft ?? data.trialDays })}</li>}
    </ul>
  );
};

/** Where the gardener stands with billing, and the plans or Stripe's portal. */
export const SubscribePage: React.FC = () => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  // Only a read with nothing to show is a failure here: a background re-read
  // that failed (after a resume, or on coming back to the tab) used to swap
  // the whole page, and its "paused until" or "resumed", for loadFailed.
  const { data, isLoading, error: billingError } = useBilling();
  const [going, setGoing] = React.useState<BillingInterval | 'portal' | 'cancel' | 'resume' | null>(null);
  // Back from Stripe to this page as it was left: its buttons usable again.
  usePageRestored(() => setGoing(null));
  const [failed, setFailed] = React.useState<string | null>(null);
  const [said, setSaid] = React.useState<string | null>(null);
  /** The pause offer: closed, opened by going to cancel, or opened by the price notice email's link. */
  const [pauseOpen, setPauseOpen] = React.useState<'closed' | 'cancel' | 'email'>('closed');

  const access = data?.access;
  /** The server's answer: "Cancel subscription" is offered (a running monthly plan, trialing included, not ending, no pause). */
  const canOfferCancel = access?.canCancel === true;
  /** The server's answer (access.canChoosePlan): a plan to pay for is offered (Choose, the after-trial line); not to a founder in their free year (B69). */
  const offerPlans = access?.canChoosePlan !== false;
  /** The server's answer: going to cancel offers a pause first (Stripe status active only); otherwise straight to cancelling. */
  const canOfferPause = access?.canPause === true;

  // The price notice email's "Pause instead" link: /subscribe?pause=1 opens the offer, once.
  React.useEffect(() => {
    if (params.get('pause') !== '1' || !data) return;
    if (canOfferPause) setPauseOpen('email');
    const next = new URLSearchParams(params);
    next.delete('pause');
    setParams(next, { replace: true });
  }, [params, setParams, data, canOfferPause]);

  /**
   * Leaves for Stripe: a checkout for a plan, the portal, or the portal's cancel step ('cancel', as the pause
   * offer's "Cancel anyway" opens it); says so here if it cannot start.
   */
  const go = async (what: BillingInterval | 'portal' | 'cancel') => {
    setGoing(what);
    setFailed(null);
    try {
      const url = what === 'portal' ? await openPortal() : what === 'cancel' ? await openPortal('cancel') : await startCheckout(what);
      window.location.assign(url);
    } catch (error) {
      setFailed(errorMessage(error, t('billing.startFailed')));
      setGoing(null);
    }
  };

  /** Takes the pause away (charged from today once it has begun), then shows where they stand. */
  const resume = async () => {
    setGoing('resume');
    setFailed(null);
    try {
      await resumePlan();
      await queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
      setSaid(t('billing.pause.resumed'));
    } catch (error) {
      setFailed(errorMessage(error, t('billing.pause.failed')));
    } finally {
      setGoing(null);
    }
  };

  /** Closes the pause offer. */
  const closePause = React.useCallback(() => setPauseOpen('closed'), []);
  /** Says the pause went in. */
  const paused = React.useCallback((until: string) => setSaid(t('billing.pause.done', { date: pauseDay(until, language) })), [t, language]);

  const heading = 'font-semibold font-body';
  const plans = data?.plans;
  const yearPrice = plans?.year;
  const monthPrice = plans?.month;
  const pausedNow = access?.paused === true;
  const pauseAhead = access?.pauseAhead === true;
  /*
   * What resuming a paused plan charges today: its month as Stripe charges
   * it, a first year's discount taken off (the server's pause offer works it
   * out), never the list price. Nothing said until it is in; no price on a failure.
   */
  const { data: pausedOffer, isPending: resumePricePending } = useQuery({
    queryKey: PAUSE_OFFER_QUERY_KEY,
    queryFn: getPauseOffer,
    enabled: pausedNow,
    retry: false,
  });
  const resumePrice = pausedOffer?.price ?? null;
  /** Someone else's garden: the owner's plan, which nothing here may change (B23). */
  const tendingFor = useTendingFor();

  return (
    <div className="mx-auto max-w-2xl p-4">
      <Link to="/plants" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('billing.backToPlants')}
      </Link>
      <h1 className={cn(heading, 'text-2xl')}>{t('billing.title')}</h1>

      {isLoading ? (
        <div className="flex items-center gap-3 py-8 text-sm text-muted-foreground"><Spinner size={20} />{t('billing.loading')}</div>
      ) : !data || !access || !yearPrice || !monthPrice ? (
        // On our side: the standard sentence; the request limit: its sentence; otherwise billing.loadFailed.
        !data && isServerFailure(billingError)
          ? <ServerFailureNote error={billingError} where="Subscribe" className="mt-4 text-destructive" />
          : <p role="alert" className="mt-4 text-sm text-destructive"><FailureText text={rateLimitedText(billingError) ?? t('billing.loadFailed')} /></p>
      ) : tendingFor ? (
        // The owner's plan and the line, with no buttons: nothing here acts on the owner's subscription.
        <TendingPlan data={{ ...data, tendingFor }} />
      ) : (
        <>
          {params.get('cancelled') && (
            <p className="mt-3 rounded-sm border bg-card px-3 py-2 text-sm text-muted-foreground">{t('billing.cancelled')}</p>
          )}

          <StatusLineText data={data} />

          {said && <p role="status" className="mt-3 rounded-sm border bg-card px-3 py-2 text-sm">{said}</p>}

          {!data.configured && (
            <p className="mt-3 text-sm text-warning">{t('billing.notConfigured')}</p>
          )}

          {access.hasPlan ? (
            <div className="mt-6">
              {(pausedNow || pauseAhead) && (
                <div className="mb-5">
                  <Button onClick={() => { void resume(); }} disabled={going !== null || !data.configured}>
                    {going === 'resume' ? <Spinner size={16} color="currentColor" /> : null}
                    <span className={going === 'resume' ? 'ml-2' : ''}>{pausedNow ? t('billing.pause.resume') : t('billing.pause.dontPause')}</span>
                  </Button>
                  {pausedNow && !resumePricePending && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {resumePrice ? t('billing.pause.resumeHint', { price: formatPrice(resumePrice) }) : t('billing.pause.resumeHintNoPrice')}
                    </p>
                  )}
                </div>
              )}
              <Button variant={pausedNow || pauseAhead ? 'outline' : 'default'} onClick={() => go('portal')} disabled={going !== null || !data.configured}>
                {going === 'portal' ? <Spinner size={16} /> : null}
                <span className={going === 'portal' ? 'ml-2' : ''}>{t('billing.manage')}</span>
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">{t('billing.manageHint')}</p>
              {canOfferCancel && (
                <Button
                  variant="link"
                  className="mt-3 h-auto px-0 text-sm text-muted-foreground"
                  disabled={going !== null || !data.configured}
                  // A pause first where one may be offered; otherwise straight to Stripe's cancel step, as before.
                  onClick={() => { if (canOfferPause) setPauseOpen('cancel'); else void go('cancel'); }}
                >
                  {going === 'cancel' ? <Spinner size={14} /> : null}
                  <span className={going === 'cancel' ? 'ml-2' : ''}>{t('billing.pause.cancel')}</span>
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Yearly first: the best value, the launch price or the founder price said plainly. */}
              <div className="mt-6 rounded-sm border border-primary bg-card p-4">
                {/* While the launch price runs, the landing page's off-season pill says so here too (owner, 2026-10-13). */}
                {plans.launch && !plans.founderPrice && (
                  <Pill tone="terracotta" icon={<Snowflake />} className="mb-2">
                    {t('billing.plan.offSeasonPill', { date: pauseDay(`${plans.launch.until}T12:00:00`, language) })}
                  </Pill>
                )}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className={cn(heading, 'text-lg')}>{t('billing.plan.yearly')}</p>
                  {data.plans.display.bestValue && <Pill size="sm" tone="solid">{t('billing.plan.bestValue')}</Pill>}
                </div>
                <p className="mt-1 text-xl sm:text-2xl">
                  {data.plans.display.monthly
                    ? t('billing.plan.yearlyPrice', {
                      price: formatPrice(yearPrice),
                      perMonth: formatPrice({ amount: plans.year.perMonth, currency: yearPrice.currency }),
                    })
                    : plans.founderPriceWords ?? formatPrice(yearPrice)}
                </p>
                {data.plans.display.line === 'founder' ? null : plans.founderPrice ? (
                  <p className="mt-1 text-sm text-muted-foreground">{t('billing.plan.founderKept')}</p>
                ) : plans.launch?.words ? (
                  <p className="mt-1 text-sm text-muted-foreground">{plans.launch.words}</p>
                ) : null}
                {data.plans.display.saving && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('billing.plan.saves', { amount: formatPrice({ amount: data.plans.display.savingAmount, currency: yearPrice.currency }) })}
                  </p>
                )}
                {/* A founder is free until the founding day (the status line above says so): nothing to choose or pay yet. */}
                {offerPlans && (
                  <Button className="mt-4 w-full sm:w-auto" disabled={going !== null || !data.configured} onClick={() => go('year')}>
                    {going === 'year' ? <Spinner size={16} color="currentColor" /> : <Check className="mr-2 h-4 w-4" aria-hidden="true" />}
                    {t('billing.choose')}
                  </Button>
                )}
              </div>
              {data.plans.display.monthly && (
                <Button
                  variant="link"
                  className="mt-2 h-auto px-0 text-sm"
                  disabled={going !== null || !data.configured}
                  onClick={() => go('month')}
                >
                  {going === 'month' ? <Spinner size={14} /> : null}
                  <span className={going === 'month' ? 'ml-2' : ''}>{t('billing.plan.orMonthly', { price: formatPrice(monthPrice) })}</span>
                </Button>
              )}

              <PlanLines data={data} year={yearPrice} month={monthPrice} />

              <PlanComparison className="mt-6" rows={plans.rows} free={plans.free} />
            </>
          )}

          {failed && <p role="alert" className="mt-3 text-sm text-destructive"><FailureText text={failed} /></p>}

          <ul className="mt-8 space-y-1 text-sm text-muted-foreground">
            <li>{t('billing.notes.cancel')}</li>
            {offerPlans && <li>{t('billing.notes.afterTrial', { plants: access.limits.plants.limit })}</li>}
            <li>{t('billing.notes.stripe')}</li>
          </ul>

          {/* A gift code to redeem, or one to give (/gift). */}
          <GiftCodeBox className="mt-8" />

          <PauseOffer open={pauseOpen !== 'closed'} cancelling={pauseOpen === 'cancel'} onClose={closePause} onPaused={paused} />
        </>
      )}
    </div>
  );
};

/** The subscribe page, as the route's default import. */
export default SubscribePage;
